<script setup lang="ts">
import IconCheck from './IconCheck.vue'
import IconChevronDown from './IconChevronDown.vue'
import { useSiteLanguages } from '~/composables/useSiteLanguages'

const auth = useAuthStore()
const modals = useModalsStore()
const currency = useCurrencyStore()

const { locale } = useI18n()
const { site } = useSite()
const { languages } = useSiteLanguages()

const initials = computed(() => {
  if (!auth.user) return undefined

  const { firstName, lastName } = auth.user

  return `${firstName[0]}${lastName[0]}`
})

const userDropdownOpen = ref(false)

const regionDropdownOpen = ref(false)

const currencies = computed(() => currency.list ?? [])
const hasMultipleCurrencies = computed(() => currencies.value.length > 1)
const selectedCurrencyCode = computed(() => currency.selected?.code)
const selectedCurrencyLabel = computed(() =>
  [currency.selected?.code, currency.selected?.sign].filter(Boolean).join(' '),
)

// The design system only ships flags for the currencies whose issuing
// country is also one of our site locales; every other currency falls back
// to its sign as a text mark (see Currency Selector Concepts).
const CURRENCY_FLAG_LOCALE: Record<string, string> = {
  GBP: 'en',
  DKK: 'da',
  NOK: 'nn',
  SEK: 'se',
}

const currencyFlag = (code: string) => CURRENCY_FLAG_LOCALE[code]

// BOAT-2013: the new-visitor balloon goes once the selector is opened by
// any route — hover, focus and click all set regionDropdownOpen.
const { visible: currencyToastVisible, dismiss: dismissCurrencyToast } =
  useCurrencyToast()
watch(regionDropdownOpen, (open) => {
  if (open) dismissCurrencyToast()
})
const onRegionFocusOut = (event: FocusEvent) => {
  if (
    !(event.currentTarget as HTMLElement).contains(
      event.relatedTarget as Node | null,
    )
  ) {
    regionDropdownOpen.value = false
  }
}
</script>

<template>
  <div class="app-menu-desktop">
    <AppMenuDesktopNav />

    <NuxtLink to="/about-us" class="app-menu-desktop__static-link text-button">
      {{ $t('string.About') }}
    </NuxtLink>

    <NuxtLink to="/faqs" class="app-menu-desktop__faqs-link">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 39.04 24.5">
        <path
          d="M34.34,0H4.16L0,4.48V15.74l4.7,4.52H21.55V24.5L27,20.26h7.9L39,15.77V4.52Z"
          fill="#fff"
        />
      </svg>

      <span>FAQs</span>
    </NuxtLink>

    <div
      class="app-menu-desktop__country-button"
      @mouseover="regionDropdownOpen = true"
      @focusin="regionDropdownOpen = true"
      @focusout="onRegionFocusOut"
      @keydown.esc="regionDropdownOpen = false"
      @mouseleave="regionDropdownOpen = false"
    >
      <button
        type="button"
        class="app-menu-desktop__region-toggle"
        :class="{
          'app-menu-desktop__region-toggle--with-currencies':
            hasMultipleCurrencies,
        }"
        :aria-label="
          hasMultipleCurrencies
            ? `${$t('string.Language')} / ${$t('string.Currency')}: ${selectedCurrencyLabel}`
            : $t('string.Language')
        "
        :aria-expanded="regionDropdownOpen"
        aria-controls="region-options"
        @click="regionDropdownOpen = true"
      >
        <img :src="`/images/flags/${locale}.png`" :alt="site.name" />
        <template v-if="hasMultipleCurrencies">
          <span class="app-menu-desktop__region-currency" dir="ltr">
            {{ selectedCurrencyLabel }}
          </span>
          <IconChevronDown
            class="app-menu-desktop__region-chevron"
            :class="{ 'is-open': regionDropdownOpen }"
            aria-hidden="true"
          />
        </template>
      </button>

      <AppCurrencyToast
        v-if="hasMultipleCurrencies && currencyToastVisible"
        arrow="right"
        @dismiss="dismissCurrencyToast"
      />

      <Transition name="fade">
        <div
          v-show="regionDropdownOpen"
          id="region-options"
          class="app-menu-desktop__nav-item__dropdown"
          :class="{
            'app-menu-desktop__region-dropdown--with-currencies':
              hasMultipleCurrencies,
          }"
        >
          <div class="app-menu-desktop__region-column">
            <div class="app-menu-desktop__region-column-title">
              {{ $t('string.Language') }}
            </div>

            <ul>
              <li v-for="language in languages" :key="language.hash">
                <NuxtLink
                  :to="language.link"
                  class="text-secondary-nav"
                  :class="{
                    'app-menu-desktop__region-item--selected':
                      language.code === locale,
                  }"
                >
                  <img
                    :src="`/images/flags/${language.code}.png`"
                    :alt="language.name"
                    class="app-menu-desktop__region-flag"
                  />

                  <span>{{ language.name }}</span>
                </NuxtLink>
              </li>
            </ul>
          </div>

          <div
            v-if="hasMultipleCurrencies"
            class="app-menu-desktop__region-column"
          >
            <div class="app-menu-desktop__region-column-title">
              {{ $t('string.Currency') }}
            </div>

            <ul>
              <li
                v-for="currencyOption in currencies"
                :key="currencyOption.code"
              >
                <button
                  type="button"
                  :aria-pressed="currencyOption.code === selectedCurrencyCode"
                  :disabled="currency.isSelecting"
                  class="text-secondary-nav app-menu-desktop__currency-item"
                  :class="{
                    'app-menu-desktop__region-item--selected':
                      currencyOption.code === selectedCurrencyCode,
                  }"
                  @click="currency.select(currencyOption.code)"
                >
                  <span
                    class="app-menu-desktop__region-flag app-menu-desktop__currency-mark"
                    :style="
                      currencyFlag(currencyOption.code)
                        ? {
                            backgroundImage: `url(/images/flags/${currencyFlag(currencyOption.code)}.png)`,
                          }
                        : undefined
                    "
                    >{{
                      currencyFlag(currencyOption.code)
                        ? ''
                        : currencyOption.sign
                    }}</span
                  >

                  <span class="app-menu-desktop__currency-code">{{
                    currencyOption.code
                  }}</span>
                  <span class="app-menu-desktop__currency-name">{{
                    currencyOption.name
                  }}</span>

                  <IconCheck
                    v-if="currencyOption.code === selectedCurrencyCode"
                  />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </div>

    <div
      class="app-menu-desktop__user"
      @mouseover="userDropdownOpen = true"
      @mouseleave="userDropdownOpen = false"
    >
      <AppAvatar
        v-if="auth.isAuthenticated"
        :initials="initials"
        color="white"
      />

      <IconUser v-else />

      <Transition name="fade">
        <div
          v-show="userDropdownOpen"
          class="app-menu-desktop__nav-item__dropdown"
        >
          <ul>
            <template v-if="auth.isAuthenticated">
              <li>
                <NuxtLink
                  href="/account/personal-details"
                  class="text-secondary-nav"
                >
                  <IconUser />
                  {{ $t('string.Personal-details') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink href="/account/password" class="text-secondary-nav">
                  <IconPassword />
                  {{ $t('string.Password') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink href="/account/tickets" class="text-secondary-nav">
                  <IconTicket />
                  {{ $t('string.Tickets') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink href="/account/favourites" class="text-secondary-nav">
                  <IconLike />
                  {{ $t('string.Favourites') }}
                </NuxtLink>
              </li>

              <li>
                <a
                  role="button"
                  class="text-secondary-nav"
                  @click="auth.logout"
                >
                  <IconLogout />
                  {{ $t('string.Logout') }}
                </a>
              </li>
            </template>

            <template v-else>
              <li>
                <a
                  role="button"
                  class="text-secondary-nav"
                  @click="modals.openLoginModal"
                >
                  <IconLogout />
                  {{ $t('string.Login') }}
                </a>
              </li>

              <li>
                <a
                  role="button"
                  class="text-secondary-nav"
                  @click="modals.openRegisterModal"
                >
                  <IconUser />
                  {{ $t('string.Register') }}
                </a>
              </li>
            </template>
          </ul>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-menu-desktop {
  flex: 1 1 0%;
  display: flex;
  align-items: center;
}

.app-menu-desktop__static-link {
  flex-shrink: 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3.75rem;
  margin-left: 0.75rem;
  margin-right: 0.5rem;
  color: currentColor;
  text-decoration: none !important;
  white-space: nowrap;

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 0.25rem;
    margin-top: 0.375rem;
    background-color: currentColor;
    opacity: 0;
    pointer-events: none;
    @include transition('opacity');
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  @include media-breakpoint-up(xxl) {
    margin-left: 1.25rem;
  }
}

.app-menu-desktop__faqs-link {
  flex-shrink: 0;
  position: relative;
  display: block;
  margin-top: 0.25rem;
  margin-left: 0.75rem;

  svg {
    width: 2.5rem;
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    font-family: $font-barlow;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.65;
    letter-spacing: 0.08333333em;
    text-align: center;
    text-indent: 0.1em;
    color: $black;
  }
}

.app-menu-desktop__country-button {
  flex-shrink: 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3.75rem;
  margin-left: 1.25rem;

  img {
    display: block;
    width: 2rem;
    height: 2rem;
    object-fit: cover;
    border-radius: 50%;
    overflow: hidden;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 0.25rem;
    margin-top: 0.375rem;
    background-color: currentColor;
    opacity: 0;
    pointer-events: none;
    @include transition('opacity');
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -0.625rem;
    left: -0.75rem;
    right: -0.75rem;
    height: 100%;
    display: none;
  }

  &:hover {
    &::before {
      opacity: 1;
    }

    &::after {
      display: block;
    }
  }

  & > svg {
    width: 1.75rem;
    height: 1.75rem;
  }

  .app-menu-desktop__nav-item__dropdown {
    left: auto;
    right: -0.75rem;
    width: 17.5rem;
    padding: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);

    &.app-menu-desktop__region-dropdown--with-currencies {
      width: 35rem;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
  }

  .app-menu-desktop__region-toggle {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &--with-currencies {
      gap: 0.625rem;
      padding: 0.25rem 0.375rem;
      border-radius: 0.25rem;
      @include transition('background-color');

      &:hover,
      &:focus-visible {
        background-color: rgba($white, 0.1);
      }
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 3px;
    }
  }

  .app-menu-desktop__region-currency {
    font-family: $font-barlow;
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.0625rem;
    white-space: nowrap;
  }

  .app-menu-desktop__region-chevron {
    flex-shrink: 0;
    width: 0.75rem;
    height: 0.75rem;
    @include transition('transform');

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .app-menu-desktop__region-column {
    padding: 0.5rem 0 0.875rem;
    max-height: calc(100vh - 8rem);
    overflow-y: auto;

    &:first-child:not(:only-child) {
      border-right: 1px solid $gray-200;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      margin-bottom: 0;
    }

    a,
    button {
      display: flex;
      align-items: center;
      width: 100%;
      border: 0;
      background: transparent;
      text-align: left;
      min-height: 2.25rem;
      padding: 0.5625rem 1.25rem;
      line-height: 1.25;
      text-decoration: none !important;
      @include transition('color, background-color');

      &:hover {
        color: $tint-red-hover;
      }
    }
  }

  .app-menu-desktop__region-column-title {
    padding: 0.875rem 1.25rem 0.5rem;
    font-family: $font-barlow;
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    color: $gray-400;
  }

  .app-menu-desktop__region-column .app-menu-desktop__region-item--selected {
    background-color: $gray-100;
  }

  .app-menu-desktop__region-flag {
    flex-shrink: 0;
    width: 1.625rem;
    height: 1.625rem;
    margin-right: 0.75rem;
    border-radius: 50%;
    background-color: $gray-200;
    background-size: cover;
    background-position: center;
  }

  .app-menu-desktop__currency-item {
    cursor: pointer;

    svg {
      flex-shrink: 0;
      width: 1rem;
      height: 1rem;
      margin-left: auto;
    }
  }

  .app-menu-desktop__currency-mark {
    justify-content: center;
    align-items: center;
    display: flex;
    font-family: $font-barlow;
    font-weight: 600;
    font-size: 0.8125rem;
    color: $gray-400;
  }

  .app-menu-desktop__currency-code {
    margin-right: 0.5rem;
    font-weight: 600;
  }

  .app-menu-desktop__currency-name {
    margin-right: 0.5rem;
    overflow: hidden;
    color: $gray-400;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  @include media-breakpoint-up(xxl) {
    margin-left: 2.5rem;
  }
}

.app-menu-desktop__user {
  flex-shrink: 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3.75rem;
  margin-left: 1.25rem;

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 0.25rem;
    margin-top: 0.375rem;
    background-color: currentColor;
    opacity: 0;
    pointer-events: none;
    @include transition('opacity');
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -0.625rem;
    left: -0.75rem;
    right: -0.75rem;
    height: 100%;
    display: none;
  }

  &:hover {
    &::before {
      opacity: 1;
    }

    &::after {
      display: block;
    }
  }

  & > svg {
    width: 1.75rem;
    height: 1.75rem;
  }

  .app-menu-desktop__nav-item__dropdown {
    left: auto;
    right: -0.75rem;
    width: 15.75rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    a {
      display: flex;
      align-items: center;
      min-height: 2.25rem;
      padding: 0.375rem 0.625rem;
      line-height: 1.25;
      text-decoration: none !important;
      border-radius: 0.125rem;
      @include transition('color');

      svg {
        flex-shrink: 0;
        width: 1.125rem;
        height: 1.125rem;
        margin-right: 1rem;
        margin-top: 1px;
      }

      &:hover {
        color: $tint-red-hover;
      }
    }
  }

  @include media-breakpoint-up(xxl) {
    margin-left: 2.5rem;
  }
}
</style>
